/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import * as React from 'react';

import { Route, RouteComponentProps, useHistory, useParams } from 'react-router-dom';

import genericErrorImg from '../assets/error-generic.png';
import { WidgetHost } from '../hosts/WidgetHost';
import { EntryPoint, ScenarioName } from '../logger';
import {
    CreateRequestMenuProps,
    MobileCreateRequestMenu,
} from '../mobile/components/MobileCreateRequestMenu/MobileCreateRequestMenu';
import { MobileCreateRequestMenuV2 } from '../mobile/components/MobileCreateRequestMenuV2/MobileCreateRequestMenuV2';
import { MobileApprovalsApp } from '../mobile/MobileApprovalsApp/MobileApprovalsApp';
import { ApprovalCreator, ApprovalType, WebhookBaseDataObject } from '../service/gen/esign';
import { isEntryMenuEnabled, isMobileRequestPageV2 } from '../utilities/ECSConfigUtils';
import { getAppHost } from '../utilities/TeamsUtils';
import { isTemplateEnabled } from '../utilities/Utils';
import { ApprovalsApp } from '../views/containers/ApprovalsApp';
import { ChooseTemplate } from '../views/containers/ChooseTemplate';
import { ChooseTemplateContainerProps } from '../views/containers/ChooseTemplate/ChooseTemplate.container';
import { CreateRequest } from '../views/containers/CreateRequest';
import { CreateRequestProps } from '../views/containers/CreateRequest/CreateRequest.types';
import {
    CreateRequestSwitcher,
    CreateRequestSwitcherProps,
} from '../views/containers/CreateRequestSwitcher/CreateRequestSwitcher';
import { CreateTemplateApproval } from '../views/containers/CreateTemplateApproval';
import { CreateTemplateApprovalContainerProps } from '../views/containers/CreateTemplateApproval/CreateTemplateApproval.container';
import { CreateTemplateInstance } from '../views/containers/CreateTemplateInstance';
import { ViewResultModal } from '../views/containers/DataExport/ViewResultModal';
import { ViewResultModalProps } from '../views/containers/DataExport/ViewResultModal/ViewResultModal.container';
import { ErrorPage, ErrorPageProps } from '../views/containers/ErrorPage';
import { NewRequestPageEmptyState } from '../views/containers/NewRequestPageEmptyState';
import { NewRequestPageEmptyStateProps } from '../views/containers/NewRequestPageEmptyState/NewRequestPageEmptyState';
import { TemplateManagement } from '../views/containers/TemplateManagement';
import { ViewDetails } from '../views/containers/ViewDetails';
import { ViewDetailsProps } from '../views/containers/ViewDetails/ViewDetails.container';
import { ViewDetailsV2 } from '../views/containers/ViewDetailsV2';
import { ViewDetailsV2ContainerProps } from '../views/containers/ViewDetailsV2/ViewDetailsV2.container';
import { CreateEsignApprovalChatCallback } from '../views/Esign/EsignChatCallback/CreateEsignApprovalChatCallback';
import { DocusignRedirectionComponent } from '../views/Esign/EsignIframe/DocusignRedirection.component';
import { AdobeLogout } from '../views/Esign/utils/Authentication/AdobeLogout';
import { AuthLoginEnd } from '../views/Esign/utils/Authentication/AdobeOAuthEnd';
import { AuthLoginStart } from '../views/Esign/utils/Authentication/AdobeOAuthStart';
import { DocusignLogoutEnd } from '../views/Esign/utils/Authentication/DocusignLogoutEnd';
import { DocusignLogoutStart } from '../views/Esign/utils/Authentication/DocusignLogoutStart';
import { DocusignAuthLoginEnd } from '../views/Esign/utils/Authentication/DocusignOAuthEnd';
import { DocusignAuthLoginStart } from '../views/Esign/utils/Authentication/DocusignOauthStart';
import { Paths } from './enums/Paths';

const ViewDetailsWithRoute: React.FC = (): JSX.Element => {
    const { approvalId, approvalType, flowEnvironment } = useParams<any>();
    return <ViewDetails approvalId={approvalId} approvalType={approvalType} flowEnvironment={flowEnvironment} />;
};

const MobileViewDetailsNavigationWithRoute: React.FC = (): JSX.Element => {
    const params = new URLSearchParams(location.search);
    const approvalId = params.get('approvalID');
    const approvalType = params.get('approvalType');
    let flowEnvironment = params.get('environmentId');

    // Cast 'null' value to null for the APIs to properly handle it
    if (flowEnvironment === 'null') {
        flowEnvironment = null;
    }

    const detailsProps: ViewDetailsProps = {
        approvalId,
        showNavigationBar: true,
        approvalType,
        flowEnvironment,
    };

    return <ViewDetails {...detailsProps} />;
};

const MobileViewDetailsV2NavigationWithRoute: React.FC = (): JSX.Element => {
    const params = new URLSearchParams(location.search);
    const templateUrl = params.get('templateUrl');
    const dataPayloadUrl = params.get('dataPayloadUrl');
    const approvalType = params.get('approvalType');

    const viewDetailsProps: ViewDetailsV2ContainerProps = {
        templateUrl,
        dataPayloadUrl,
        approvalType,
    };

    return <ViewDetailsV2 {...viewDetailsProps} />;
};

const MobileUnsupportedNavigation: React.FC = (): JSX.Element => {
    const params = new URLSearchParams(location.search);
    const redirectUri = params.get('redirectUri');
    const errorMessage = params.get('errorMessage');
    const allowedUrls = [
        'https://vnext-test.msapprovals.microsoft.com',
        'https://vnext-uat.msapprovals.microsoft.com',
        'https://vnext.msapprovals.microsoft.com',
    ];
    const isAllowedUrl = (url: string | null): boolean => {
        // check for the exact match against the allowed URLs
        return allowedUrls.includes(url?.trim());
    };
    const openLinkInNewTab = (): void => {
        if (redirectUri && isAllowedUrl(redirectUri)) {
            const newWindow = window.open(redirectUri, '_blank', 'noopener noreferrer nofollow');
            if (newWindow) {
                newWindow.opener = null;
            }
        } else {
            redirectToDefaultUrl();
        }
    };

    const history = useHistory();
    const redirectToDefaultUrl = (): void => {
        history.push(Paths.BasePath + Paths.ApprovalsApp);
    };

    let errorPageProps: ErrorPageProps = {
        image: genericErrorImg,
        message: errorMessage ?? getAppHost().getLocale().strings.view_approval_on_desktop_or_web,
    };

    if (redirectUri) {
        errorPageProps = {
            ...errorPageProps,
            onAction: openLinkInNewTab,
            actionButtonText: getAppHost().getLocale().strings.open_link_in_web,
        };
    }

    return <ErrorPage {...errorPageProps} />;
};

const AuthLoginEndWithParams: React.FC = (): JSX.Element => {
    const params = new URLSearchParams(location.search);
    const code = params.get('code');
    return <AuthLoginEnd code={code} />;
};

const DocusignAuthLoginEndWithParams: React.FC = (): JSX.Element => {
    const params = new URLSearchParams(location.search);
    const code = params.get('code');
    const state = params.get('state');
    return <DocusignAuthLoginEnd state={state} code={code} />;
};

const renderCreateReqeustSwitcher = (props: RouteComponentProps<any, any, CreateRequestSwitcherProps>): JSX.Element => {
    const host = getAppHost();
    if (
        isTemplateEnabled() &&
        host.getEcsConfig().MicrosoftTeamApprovals.ShowNewRequestPageToggleButton &&
        !getAppHost().isNewChat()
    ) {
        if (host instanceof WidgetHost) {
            return (
                <CreateRequestSwitcher
                    {...props.location.state}
                    onDismiss={host.onClose.bind(host)}
                    showPivot={false}
                />
            );
        } else {
            return <CreateRequestSwitcher {...props.location.state} showNavigationBar={isPersonalApp()} />;
        }
    } else {
        return renderCreateReqeust(props);
    }
};

const EsignChatCallback: React.FC = (): JSX.Element => {
    const params = new URLSearchParams(location.search);
    const webhookDataObject: WebhookBaseDataObject = {
        ChannelId: params.get('channelId'),
        ConversationId: params.get('conversationId'),
        AadGroupId: params.get('aadGroupId'),
        Culture: params.get('locale'),
        MRI: params.get('mri'),
        ServiceUrl: params.get('serviceUrl'),
        ApprovalCreator: ApprovalCreator[params.get('approvalCreator')],
        Type: 'Creation',
        ApprovalType: ApprovalType.ESign,
    };
    return <CreateEsignApprovalChatCallback webhookDataObject={webhookDataObject} />;
};

// utility function to check if the app is in personal app.
const isPersonalApp = (): boolean => getAppHost().isGlobalContext();

const renderCreateReqeust = (props: RouteComponentProps<any, any, CreateRequestProps>): JSX.Element => {
    const host = getAppHost();
    if (host instanceof WidgetHost) {
        return <CreateRequest {...props.location.state} onDismiss={host.onClose.bind(host)} showPivot={false} />;
    } else {
        return <CreateRequest {...props.location.state} showNavigationBar={isPersonalApp()} />;
    }
};

const renderCreateTemplateApproval = (
    props: RouteComponentProps<any, any, CreateTemplateApprovalContainerProps>
): JSX.Element => {
    return <CreateTemplateApproval {...props.location.state} />;
};

const renderChooseTemplate = (props: RouteComponentProps<any, any, ChooseTemplateContainerProps>): JSX.Element => {
    return <ChooseTemplate {...props.location.state} />;
};

const renderMobileCreateRequestMenu = (props: RouteComponentProps<any, any, CreateRequestMenuProps>): JSX.Element => {
    if (isTemplateEnabled() && isMobileRequestPageV2()) {
        return <MobileCreateRequestMenuV2 {...props.location.state} />;
    }
    return <MobileCreateRequestMenu {...props.location.state} />;
};

const renderMobileComposeExtensionTaskModule = (props: RouteComponentProps<any, any, any>): JSX.Element => {
    // Initialize create request menu scenario, cause at present only mobile has create request menu.
    if ((isEntryMenuEnabled() && !getAppHost().isNewChat()) || isMobileRequestPageV2()) {
        const logger = getAppHost().getLogger();
        logger.createScenario(ScenarioName.CreateRequestMenu, EntryPoint.ComposeExtension);
    }

    if (isTemplateEnabled() && isMobileRequestPageV2()) {
        return (
            <MobileCreateRequestMenuV2
                {...props.location.state}
                showNaviBar={isPersonalApp() && !getAppHost().isNewChat()}
                isNewChat={getAppHost().isNewChat()}
            />
        );
    }

    return isEntryMenuEnabled ? (
        <MobileCreateRequestMenu {...props.location.state} showNaviBar={isPersonalApp() && !getAppHost().isNewChat()} />
    ) : (
        <CreateRequest {...props.location.state} showNavigationBar={isPersonalApp() && !getAppHost().isNewChat()} />
    );
};

const renderNewRequestPageEmptyState = (
    props: RouteComponentProps<any, any, NewRequestPageEmptyStateProps>
): JSX.Element => {
    return <NewRequestPageEmptyState {...props.location.state} />;
};

const renderViewResultModal = (props: RouteComponentProps<any, any, ViewResultModalProps>): JSX.Element => {
    return <ViewResultModal {...props.location.state} />;
};

const Routes = (
    <React.Fragment>
        <Route path={Paths.BasePath + Paths.GetTaskModule} render={renderCreateReqeustSwitcher} />
        <Route path={Paths.BasePath + Paths.CreateApproval} render={renderCreateReqeust} />
        <Route path={Paths.BasePath + Paths.NewRequestPageEmptyState} render={renderNewRequestPageEmptyState} />
        <Route path={Paths.BasePath + Paths.CreateTemplateApproval} render={renderCreateTemplateApproval} />
        <Route path={Paths.BasePath + Paths.Details} component={ViewDetailsWithRoute} />
        <Route path={Paths.BasePath + Paths.EsignChatCallback} component={EsignChatCallback} />
        <Route path={Paths.BasePath + Paths.ApprovalsApp} component={ApprovalsApp} />
        <Route path={Paths.BasePath + Paths.AdobeLoginStart} component={AuthLoginStart} />
        <Route path={Paths.BasePath + Paths.AdobeLoginEnd} component={AuthLoginEndWithParams} />
        <Route path={Paths.BasePath + Paths.AdobeLogout} component={AdobeLogout} />
        <Route path={Paths.BasePath + Paths.DocusignLoginStart} component={DocusignAuthLoginStart} />
        <Route path={Paths.BasePath + Paths.DocusignLoginEnd} component={DocusignAuthLoginEndWithParams} />
        <Route path={Paths.BasePath + Paths.DocusignLogoutStart} component={DocusignLogoutStart} />
        <Route path={Paths.BasePath + Paths.DocusignLogoutEnd} component={DocusignLogoutEnd} />
        <Route path={Paths.BasePath + Paths.TemplateManagement} component={TemplateManagement} />
        <Route path={Paths.BasePath + Paths.CreateTemplateInstance} render={(): any => <CreateTemplateInstance />} />
        <Route path={Paths.BasePath + Paths.DocusignRedirectionComponent} component={DocusignRedirectionComponent} />
        {/* TODO: stub route for AgentAndAppApprovals details — reuses ViewDetailsV2 component until U7 resolves */}
        <Route
            path={Paths.BasePath + Paths.AgentAndAppApprovalsDetails}
            component={MobileViewDetailsV2NavigationWithRoute}
        />
    </React.Fragment>
);

const MobileRoutes = (
    <React.Fragment>
        <Route path={Paths.BasePath + Paths.GetTaskModule} render={renderMobileComposeExtensionTaskModule} />
        <Route path={Paths.BasePath + Paths.CreateRequestMenu} render={renderMobileCreateRequestMenu} />
        <Route path={Paths.BasePath + Paths.NewRequestPageEmptyState} render={renderNewRequestPageEmptyState} />
        <Route path={Paths.BasePath + Paths.CreateApproval} render={renderCreateReqeust} />
        <Route path={Paths.BasePath + Paths.ChooseTemplate} render={renderChooseTemplate} />
        <Route path={Paths.BasePath + Paths.CreateTemplateApproval} render={renderCreateTemplateApproval} />
        <Route path={Paths.BasePath + Paths.Details} component={ViewDetailsWithRoute} />
        <Route path={Paths.BasePath + Paths.EsignChatCallback} component={EsignChatCallback} />
        <Route path={Paths.BasePath + Paths.DetailsNavigation} component={MobileViewDetailsNavigationWithRoute} />
        <Route path={Paths.BasePath + Paths.DetailsV2Navigation} component={MobileViewDetailsV2NavigationWithRoute} />
        <Route path={Paths.BasePath + Paths.ApprovalsApp} component={MobileApprovalsApp} />
        <Route path={Paths.BasePath + Paths.AdobeLoginStart} component={AuthLoginStart} />
        <Route path={Paths.BasePath + Paths.AdobeLoginEnd} component={AuthLoginEndWithParams} />
        <Route path={Paths.BasePath + Paths.AdobeLogout} component={AdobeLogout} />
        <Route path={Paths.BasePath + Paths.DocusignLoginStart} component={DocusignAuthLoginStart} />
        <Route path={Paths.BasePath + Paths.DocusignLoginEnd} component={DocusignAuthLoginEndWithParams} />
        <Route path={Paths.BasePath + Paths.DocusignLogoutStart} component={DocusignLogoutStart} />
        <Route path={Paths.BasePath + Paths.DocusignLogoutEnd} component={DocusignLogoutEnd} />
        <Route path={Paths.BasePath + Paths.UnsupportedOnMobile} component={MobileUnsupportedNavigation} />
        <Route path={Paths.BasePath + Paths.DataExportResult} render={renderViewResultModal} />
        {/* TODO: stub route for AgentAndAppApprovals details — reuses ViewDetailsV2 component until U7 resolves */}
        <Route
            path={Paths.BasePath + Paths.AgentAndAppApprovalsDetails}
            component={MobileViewDetailsV2NavigationWithRoute}
        />
    </React.Fragment>
);

export { Routes, MobileRoutes };
